import React from 'react';

import { Link } from '~/lib/react-router-dom';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, Skeleton, Typography } from '@mui/material';

import useOrderBreadcrumbs from '~/hooks/useOrderBreadcrumbs';
import useUserBreadcrumbs from '~/hooks/useUserBreadcrumbs';

import TenantMismatchAlerts from './TenantMismatchAlerts';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface UserBreadcrumbsProps {
  /** The user ID to fetch breadcrumb data for */
  userId?: string | null;
  /** The order ID to fetch breadcrumb data for (will also fetch user from order) */
  orderId?: string | null;
  /** The current page name (displayed as the last breadcrumb, not linked). If empty, the last entity becomes the current page. */
  currentPage?: string;
  /** Pre-loaded user data (use when user is already fetched) */
  user?: BreadcrumbItem | null;
  /** Pre-loaded order data (use when order is already fetched) */
  order?: BreadcrumbItem | null;
}

function BreadcrumbLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link to={href} style={{ textDecoration: 'none' }}>
      <Typography color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
        {children}
      </Typography>
    </Link>
  );
}

function CurrentPage({ children }: { children: React.ReactNode }) {
  return (
    <Typography color="text.primary" fontWeight={500}>
      {children}
    </Typography>
  );
}

export default function UserBreadcrumbs({
  userId,
  orderId,
  currentPage = '',
  user: userProp,
  order: orderProp,
}: UserBreadcrumbsProps) {
  // Fetch labels only when they were not provided. Always fetch when we have IDs so tenant/brand
  // mismatch alerts can render even if the caller already passed breadcrumb labels.
  const fetchUserForLabel = Boolean(userId) && !userProp && !orderId;
  const fetchOrderForLabel = Boolean(orderId) && !orderProp;
  const fetchUserForWarning = Boolean(userId) && !orderId;
  const fetchOrderForWarning = Boolean(orderId);

  const { data: userData, isLoading: isLoadingUser } = useUserBreadcrumbs({
    userId: userId ?? null,
    enabled: fetchUserForLabel || fetchUserForWarning,
  });

  const { data: orderData, isLoading: isLoadingOrder } = useOrderBreadcrumbs({
    orderId: orderId ?? null,
    enabled: fetchOrderForLabel || fetchOrderForWarning,
  });

  // Prefer order data (which includes user info), then user data, then props
  const user = userProp ?? orderData?.user ?? userData?.user ?? null;
  const order = orderProp ?? orderData?.order ?? null;

  const isLoading = (fetchUserForLabel && isLoadingUser) || (fetchOrderForLabel && isLoadingOrder);
  const mismatchAlerts = (
    <TenantMismatchAlerts orderBrand={orderData?.order.brand} userTenant={userData?.user.tenant} />
  );

  if (isLoading) {
    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mt: '10px', mb: 1 }}>
        <Skeleton width={50} />
        <Skeleton width={100} />
        {orderId && <Skeleton width={80} />}
        {orderId && <Skeleton width={150} />}
      </Breadcrumbs>
    );
  }

  // Determine what is the "final" item (the current page entity)
  const hasCurrentPage = currentPage.length > 0;

  const items: Array<React.ReactNode> = [];

  // Determine what's the "end" of the hierarchy
  const isUserFinal = user && !order && !hasCurrentPage;
  const isOrderFinal = order && !hasCurrentPage;

  // Always add "Users" section link if we have user data
  if (user) {
    items.push(
      <BreadcrumbLink key="users-section" href="/users">
        Users
      </BreadcrumbLink>,
    );

    // Add user name - link it unless it's the final item
    if (isUserFinal) {
      items.push(<CurrentPage key="user-name">{user.label}</CurrentPage>);
    } else if (user.href) {
      items.push(
        <BreadcrumbLink key="user-name" href={user.href}>
          {user.label}
        </BreadcrumbLink>,
      );
    }
  }

  // Add "Orders" section and order label
  if (order && !isUserFinal) {
    // Get the customer ID for the orders link
    const customerId = userId || user?.href?.split('/').pop();

    // Add "Orders" section link (links to user's orders)
    if (customerId) {
      items.push(
        <BreadcrumbLink key="orders-section" href={`/purchases/section/allOrders?customer_id=${customerId}`}>
          Orders
        </BreadcrumbLink>,
      );
    }

    // Add order label - link it unless it's the final item
    if (isOrderFinal) {
      items.push(<CurrentPage key="order-label">{order.label}</CurrentPage>);
    } else if (order.href) {
      items.push(
        <BreadcrumbLink key="order-label" href={order.href}>
          {order.label}
        </BreadcrumbLink>,
      );
    }
  }

  // Add explicit current page if provided
  if (hasCurrentPage) {
    items.push(<CurrentPage key="current">{currentPage}</CurrentPage>);
  }

  if (items.length <= 1) {
    return mismatchAlerts;
  }

  return (
    <Box>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mt: '10px', mb: 1 }}>
        {items}
      </Breadcrumbs>
      {mismatchAlerts}
    </Box>
  );
}
